import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AllowedStoreTypeEnum } from '../../../enum/type/allowed-store-type.enum';
import { DateFormatEnum } from '../../../enum/date-format.enum';
import { DateRangeEnum } from '../../../enum/date-range.enum';
import { RouteEnum } from '../../../enum/route.enum';
import { StorageConstants } from '../../../constant/storage.constant';
import { DynamicTableColumnModel } from '../../../models/dto/core/dynamicTableColumn.model';
import { SelectedItemIdDto } from '../../../models/dto/selectedItemIdDto';
import { CommonRequest } from '../../../models/rest/common/commonRequest';
import { CustomerSearchRequest } from '../../../models/rest/customer/customerSearchRequest';
import { CallerIdLogResponse } from '../../../models/rest/log/caller-id-log-response.model';
import { AuthService } from '../../../services/auth.service';
import { AlertifyService } from '../../../services/core/alertify.service';
import { ExportService } from '../../../services/core/export.service';
import { LogReportService } from '../../../services/report/log-report.service';
import { ColumnVisibleService } from '../../../services/core/column-visible.service';
import { VoipCallImportFromServerModalComponent } from '../voip-call-import-from-server-modal/voip-call-import-from-server-modal.component';
import { ModalSizeEnum } from '../../../enum/modal-size.enum';
import { SessionStorageService } from '../../../services/session-storage.service';
import { StoreCacheService } from '../../../services/cache/store-cache.service';
import { RoleCodeEnum } from '../../../enum/role-code.enum';
import { ExportDataRequest } from '../../../models/rest/export/export-data-request';
import { UserOperationalService } from '../../../services/user-operational.service';
import { ExportFileTypeEnum } from '../../../enum/common/export-file-type.enum';
import { ExcelConstant } from '../../../constant/excel.constant';
import { ArrayUtils } from '../../../utils/array.utils';
import { DateUtils } from '../../../utils/date.utils';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-callerid-history-report-modal',
  templateUrl: './callerid-history-report-modal.component.html',
  styleUrls: ['./callerid-history-report-modal.component.css']
})
export class CalleridHistoryReportModalComponent implements OnInit {
  title;
  screenCode = "callerid-history-report-modal";
  @Output() passEntry: EventEmitter<any> = new EventEmitter();

  dataList: CallerIdLogResponse[] = [];
  searchText: string = "";
  searchCriteria: CommonRequest = new CommonRequest();
  dateRange: string = DateRangeEnum.TODAY;
  dateTitle: string;
  allowedType: string = AllowedStoreTypeEnum.ALLOWED_STORE_REPORT;

  cols: DynamicTableColumnModel[];
  selectedColumns: DynamicTableColumnModel[];
  loading: boolean = false;

  stores: SelectedItemIdDto[] = [];
  selectedStores: SelectedItemIdDto[] = [];
  storeDropdownSettings = {};
  exportFileTypeEnum = ExportFileTypeEnum;

  constructor(
    public activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private sessionStorageService: SessionStorageService,
    private translate: TranslateService,
    private authService: AuthService,
    private alertifyService: AlertifyService,
    private logReportService: LogReportService,
    private exportService: ExportService,
    private storeCacheService: StoreCacheService,
    private router: Router,
    private columnVisibleService: ColumnVisibleService,
    private userOperationalService: UserOperationalService
  ) { }

  async ngOnInit() {
    this.title = this.translate.instant('menuitem.callerid-history-report');
    this.getDropdownSettings();
    this.setStartEndDate();
    this.getCols();
    this.setSelectedColumns();
    await this.getStores();
    this.getReport();
  }

  private getDropdownSettings() {
    this.storeDropdownSettings = {
      singleSelection: false,
      idField: "id",
      textField: "name",
      placeHolder: this.translate.instant('dropdown.select-store'),
      selectAllText: this.translate.instant('dropdown.select-all-text'),
      unSelectAllText: this.translate.instant('dropdown.un-select-all-text'),
      searchPlaceholderText: this.translate.instant('dropdown.search-placeholder-text'),
      itemsShowLimit: 1,
      allowSearchFilter: true,
    };
  }

  async getStores() {
    try {
      const res = await this.storeCacheService.getStoreSelectList(this.authService.getStoreId(), this.allowedType).toPromise();
      this.stores = res ?? [];
      this.selectedStores = this.stores ?? [];
    } catch (err) {
      this.alertifyService.error(err);
    }
  }

  setStartEndDate() {
    let returnValue = DateUtils.getStartEndDate(this.dateRange);
    this.searchCriteria.startDate = returnValue.startDate;
    this.searchCriteria.endDate = returnValue.endDate;
    this.searchCriteria.isDateActive = returnValue.isDateActive;
    this.dateTitle = this.dateRange === DateRangeEnum.CUSTOM ? returnValue.title : this.translate.instant('dateRangeEnumValue.' + returnValue.title);
  }

  changedDate(p) {
    this.searchCriteria.startDate = p.startDate;
    this.searchCriteria.endDate = p.endDate;
    this.searchCriteria.isDateActive = p.isDateActive;
    this.dateRange = p.dateRange;
    this.getReport();
  }

  getReport() {
    this.loading = true;
    this.searchCriteria.customerId = 0;
    this.searchCriteria.storeIds = ArrayUtils.getValuesByKey(this.selectedStores, 'id');
    this.logReportService.getCallerıdLogReport(this.searchCriteria).subscribe(
      data => {
        this.dataList = data;
        this.loading = false;
      },
      err => {
        this.loading = false;
        this.alertifyService.error(err);
      }
    );
  }

  async exportFile(fileType: string) {
    try {
      if (! await this.userOperationalService.checkAndOpenModalIfNecessary(RoleCodeEnum.EXPORT_CUSTOMER_LIST, this.authService.getStoreId(), AllowedStoreTypeEnum.ALLOWED_STORE_REPORT)) {
        return;
      }
      let request = new ExportDataRequest();
      request.fileName = this.title;
      request.fileType = fileType;
      request.data = this.dataList;
      request.columns = this.exportService.convertToExportColumns(this.selectedColumns);
      request.customerId = this.searchCriteria.customerId;
      request.isDateActive = this.searchCriteria.isDateActive;
      request.startDate = this.searchCriteria.startDate;
      request.endDate = this.searchCriteria.endDate;
      request.isLandscape = true;
      await this.exportService.exportFile(request);
    } catch (e) {
      this.alertifyService.error(e);
    }
  }

  onRowDblClick(rowData) {
    this.activeModal.close();
    if (rowData.customerId > 0) {
      this.router.navigate([`${RouteEnum.CUSTOMER_DETAIL}/${rowData.customerId}`], { skipLocationChange: false });
    } else {
      let customerSearchRequest = new CustomerSearchRequest();
      customerSearchRequest.phone = rowData.incomingNo;
      this.sessionStorageService.setJSONData(StorageConstants.SEARCH_CRITERIA, customerSearchRequest);
      this.router.navigate([RouteEnum.CUSTOMER_ADD], { skipLocationChange: false });
    }
  }

  openVoipCallImport() {
    const modalRef = this.modalService.open(VoipCallImportFromServerModalComponent, {
      backdrop: "static", centered: true, scrollable: false, size: ModalSizeEnum.MEDIUM
    });
    modalRef.componentInstance.passEntry.subscribe((receivedEntry) => {
      if (receivedEntry) {
        this.getReport();
      }
      modalRef.close();
    });
  }

  private getCols() {
    this.cols = [
      {
        field: "creationDate",
        header: this.translate.instant('common.creationDate'),
        isSelected: true,
        dataType: "date",
        dateFormat: DateFormatEnum.DATE_TIME_FORMAT_DISPLAY,
        isBold: true
      },
      {
        field: "incomingNo",
        header: this.translate.instant('communication.phone'),
        dataType: "phone",
        isSelected: true,
      },
      {
        field: "internalNo",
        header: this.translate.instant('voip.internalNo'),
        isSelected: true,
      },
      {
        field: "customerName",
        isPrivateData: true,
        header: this.translate.instant('customer.customerName'),
        isSelected: true,
        excelWidth: 25,
        excelStyle: ExcelConstant.BOLD_STYLE
      },
      {
        field: "storeName",
        header: this.translate.instant('store.storeName'),
        isSelected: false,
      },
      {
        field: "isIncomingCall",
        header: this.translate.instant('callerIdLog.isIncomingCall'),
        dataType: 'boolean',
        isSelected: true,
      },
      {
        field: "isDone",
        header: this.translate.instant('callerIdLog.isDone'),
        dataType: 'boolean'
      },
      {
        field: "userName",
        header: this.translate.instant('user.userName'),
        isSelected: true,
      },
    ];
  }

  setSelectedColumns() {
    this.selectedColumns = this.columnVisibleService.prepareSelectedColumns(this.screenCode, null, this.cols);
  }

  close() {
    this.activeModal.close();
  }
}
